import { useFlags } from "launchdarkly-react-client-sdk";

// Default to false for easier testing - change to true for production
const defaultValue = false;

/**
 * Hook to get a boolean feature flag value from LaunchDarkly
 * @param key - The feature flag key
 * @returns The boolean value of the feature flag, defaults to false if not available
 */
export function useBooleanFlag(key: string): boolean {
  const flags = useFlags();

  if (!flags) {
    return defaultValue;
  }

  return flags[key] ?? defaultValue;
}

/**
 * Hook to get a generic feature flag value from LaunchDarkly
 * @param key - The feature flag key
 * @returns The value of the feature flag or undefined if not available
 */
export function useFlag<T>(key: string): T | undefined {
  return useFlags()?.[key];
}

/**
 * Feature flag key for self-triggered questions feature
 * Note: LaunchDarkly converts kebab-case to camelCase in the SDK
 * Dashboard flag key: repath-self-triggered-questions-web
 * SDK flag key: repathSelfTriggeredQuestionsWeb
 */
export const FEATURE_FLAG_SELF_TRIGGERED_QUESTIONS_WEB =
  "repathSelfTriggeredQuestionsWeb";

/**
 * Hook to check if self-triggered questions feature is enabled
 * @returns true if the feature is enabled, false otherwise
 */
export const useSelfTriggeredQuestionsWebFlag = () =>
  useBooleanFlag(FEATURE_FLAG_SELF_TRIGGERED_QUESTIONS_WEB);

/**
 * Feature flag key for the new multi-screen question forms experience
 * Note: LaunchDarkly converts kebab-case to camelCase in the SDK
 * Dashboard flag key: question-forms-participant-webapp
 * SDK flag key: questionFormsParticipantWebapp
 */
export const FEATURE_FLAG_QUESTION_FORMS_PARTICIPANT_WEBAPP =
  "questionFormsParticipantWebapp";

/**
 * Hook to check if the new question forms experience is enabled
 * @returns true if the feature is enabled, false otherwise (defaults to off)
 */
export const useQuestionFormsParticipantWebappFlag = () =>
  useBooleanFlag(FEATURE_FLAG_QUESTION_FORMS_PARTICIPANT_WEBAPP);

/**
 * Feature flag key for participant <-> officer chat in the webapp
 * Note: LaunchDarkly converts kebab-case to camelCase in the SDK
 * Dashboard flag key: participant-chat-webapp
 * SDK flag key: participantChatWebapp
 */
export const FEATURE_FLAG_PARTICIPANT_CHAT_WEBAPP = "participantChatWebapp";

/**
 * Hook to check if participant chat is enabled
 * @returns true if the feature is enabled, false otherwise (defaults to off)
 */
export const useParticipantChatWebappFlag = () =>
  useBooleanFlag(FEATURE_FLAG_PARTICIPANT_CHAT_WEBAPP);

/**
 * Feature flag key for the agency Resources page
 * Note: LaunchDarkly converts kebab-case to camelCase in the SDK
 * Dashboard flag key: resources-participant-webapp
 * SDK flag key: resourcesParticipantWebapp
 */
export const FEATURE_FLAG_RESOURCES_PARTICIPANT_WEBAPP =
  "resourcesParticipantWebapp";

/**
 * Hook to check if the Resources page is enabled
 * @returns true if the feature is enabled, false otherwise (defaults to off)
 */
export const useResourcesParticipantWebappFlag = () =>
  useBooleanFlag(FEATURE_FLAG_RESOURCES_PARTICIPANT_WEBAPP);
